import React, { useEffect, useRef, useState } from "react";

import { TablesDetailsInput, useUpdateTableDetailsForUserMutation } from "gql/generated/types";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Path } from "react-hook-form";
import { TableColumn } from "utils/types";
import { exportExcel, findObjectVal } from "utils/utils";
import useProjectContext from "../hooks/useProjectContext";
import TableToolbar from "./TableToolbar";

type ItemsTableProps<T> = {
  columns: TableColumn<T>[];
  data: T[];
  searchFields?: Path<T>[];
  toolbarBtnClassName?: string;
  toolbarBtnLabel?: string;
  onToolbarBtnClick?: () => void;
  loading?: boolean;
  showToolbar?: boolean;
  showToolbarBtn?: boolean;
  tableName: string;
  setFilteredData?: (data: T[]) => void;
};
const ItemsTable = <T,>({
  columns,
  data,
  onToolbarBtnClick = () => {},
  searchFields = [],
  toolbarBtnClassName = "",
  toolbarBtnLabel,
  loading,
  showToolbar = true,
  showToolbarBtn = true,
  tableName,
  setFilteredData,
}: ItemsTableProps<T>) => {
  const { userSettings } = useProjectContext();
  const [page, setPage] = useState(0);
  const [filterItems, setFilterItems] = useState<T[]>(data);
  const [excelData, setExcelData] = useState<T[]>([]);
  const dt = useRef<DataTable>(null);
  const userTableDetails = () => userSettings?.userSettings.tablesDetails?.find(r => r?.table === tableName)

  const [updateTableDetails] = useUpdateTableDetailsForUserMutation();

  useEffect(() => {
    if(data) {
      setExcelData(data);
      dt.current?.reset();
    }
  }, [data]);

  const exportCSV = () => {
    dt.current?.exportCSV({ selectionOnly: false });
  };

  const handleExportExcel = () => {
    const tableData = excelData.map(row => {
      const newData: any = {};

      columns.forEach(col => {
        if (col.hideInFile || col.hidden) return;
        newData[col.header] = col.field ? findObjectVal(row, col.field)?.toString() : col.body;
      });
      return newData;
    });
    exportExcel(tableData, tableName);
  }

  const onPage = (e: DataTablePFSEvent) => {
    const tableDetails: TablesDetailsInput = {table: tableName, rows: e.rows, columnOrders: userTableDetails()?.columnOrders || []}
    setPage(e.first);
    if (userTableDetails()?.rows !== e.rows) {
      updateTableDetails({
      variables: { tableDetails }
      })
    }
  };

  return (
    <>
      {showToolbar && (
        <TableToolbar
          searchData={data}
          onSearch={setFilterItems}
          onClickBtn={onToolbarBtnClick}
          searchFields={searchFields}
          classNameBtn={toolbarBtnClassName}
          showBtn={showToolbarBtn}
          labelBtn={toolbarBtnLabel}
          additionalControls={
            <>
              <Button
                type="button"
                icon="pi pi-file"
                className="p-button-outlined p-button-warning"
                title="Export to CSV"
                onClick={exportCSV}
              />
              <Button
                type="button"
                icon="pi pi-file-excel"
                className="p-button-outlined p-button-warning"
                title="Export to Excel"
                onClick={handleExportExcel}
              />
            </>
          }
        />
      )}
      <DataTable
        ref={dt}
        value={filterItems}
        onValueChange={tableData => {
          setExcelData(tableData);
          setFilteredData?.(tableData);
        }}
        stripedRows
        paginator
        rows={userTableDetails()?.rows || 10}
        reorderableColumns
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        loading={loading}
        size="small"
        className="overflow-x-auto"
        first={page}
        onPage={onPage}
      >
        {columns.map(col => (
          <Column key={col.header} {...col} />
        ))}
      </DataTable>
    </>
  );
};

export default ItemsTable;
